import { useState, useEffect, useRef, useCallback } from 'react';
import { serviceContainer } from '../../../services/ServiceContainer';
import { EULA_SECTIONS, type EulaBlock } from './eulaContent';

interface AddThisPCProps {
  onAttestation: () => Promise<void>;
  onSkip?: () => void;
}

const HEADING_STYLES: Record<2 | 3 | 4, React.CSSProperties> = {
  2: { margin: '8px 0 2px', color: 'rgba(228,228,228,0.92)', fontSize: 13, lineHeight: '18px', fontWeight: 600 },
  3: { margin: '4px 0 2px', color: 'rgba(228,228,228,0.82)', fontSize: 12, lineHeight: '16px', fontWeight: 590 },
  4: { margin: '4px 0 2px', color: 'rgba(228,228,228,0.72)', fontSize: 11, lineHeight: '16px', fontWeight: 590 },
};

const EulaBlockRenderer = ({ block }: { block: EulaBlock }) => {
  if (block.type === 'heading') {
    return <p style={HEADING_STYLES[block.level]}>{block.text}</p>;
  }
  if (block.type === 'list') {
    return (
      <ul style={{ margin: 0, paddingLeft: 18, color: 'rgba(228,228,228,0.553)' }}>
        {block.items.map((item, j) => (
          <li key={j} style={{ fontSize: 11, lineHeight: '16px', fontWeight: 400, marginBottom: 2 }}>{item}</li>
        ))}
      </ul>
    );
  }
  return (
    <p style={{ margin: 0, color: 'rgba(228,228,228,0.553)', fontSize: 11, lineHeight: '16px', fontWeight: 400, whiteSpace: 'pre-line' }}>
      {block.text}
    </p>
  );
};

/** Generate a short error code from the error message for dev cross-reference */
function deriveErrorCode(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error);
  const codeMap: [RegExp | string, string][] = [
    // Timeout errors
    ['Timeout waiting for attestation data from C++', 'ATT-01'],
    ['Timeout waiting for getMachineId reply', 'ATT-02'],
    ['Timeout waiting for attestation to complete', 'ATT-05'],
    // Auth errors
    ['No Clerk token available', 'ATT-03'],
    ['No authenticated user found', 'ATT-04'],
    // C++ TPM / signing errors
    [/SIGNATURE_FAILED/i, 'ATT-10'],
    [/TPM.*not available/i, 'ATT-11'],
    [/TPM.*disabled/i, 'ATT-12'],
    [/TPM.*access denied/i, 'ATT-13'],
    [/TPM.*error/i, 'ATT-14'],
    [/NCrypt.*failed/i, 'ATT-15'],
    [/key.*not found/i, 'ATT-16'],
    [/Empty attestation signature/i, 'ATT-17'],
    [/INVALID_REQUEST/i, 'ATT-18'],
    // Network / backend errors
    [/Failed to get challenge/i, 'ATT-20'],
    [/Failed to submit attestation/i, 'ATT-21'],
    [/Attestation verification failed/i, 'ATT-22'],
    [/Incomplete attestation data/i, 'ATT-23'],
    [/No signature in attestation/i, 'ATT-24'],
    // Status check
    [/Attestation failed with status/i, 'ATT-30'],
  ];
  for (const [pattern, code] of codeMap) {
    if (typeof pattern === 'string' ? msg.includes(pattern) : pattern.test(msg)) {
      return code;
    }
  }
  // Fallback: hash to produce a unique code
  const hash = Array.from(msg.slice(0, 40)).reduce((acc, c) => acc + c.charCodeAt(0), 0) % 9999;
  return `ATT-99-${hash.toString().padStart(4, '0')}`;
}

const MAX_RETRIES = 2;

export const AddThisPC = ({ onAttestation, onSkip }: AddThisPCProps) => {
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [showScrollWarning, setShowScrollWarning] = useState(false);

  const scrollRef = useRef<HTMLDivElement>(null);
  const retriesExhausted = attempts >= MAX_RETRIES && errorCode !== null;

  useEffect(() => {
    serviceContainer.analyticsManager.sendOnboardingAddPcViewedEvent({ attestationStatus: 'pending' });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el || hasScrolledToBottom) return;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 20) {
      setHasScrolledToBottom(true);
      setShowScrollWarning(false);
    }
  }, [hasScrolledToBottom]);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, []);

  const handleAttest = async () => {
    if (!agreed || !hasScrolledToBottom || loading) return;
    setLoading(true);
    setErrorCode(null);
    const attemptNumber = attempts + 1;
    try {
      await onAttestation();
      serviceContainer.analyticsManager.sendOnboardingAddPcAttemptedEvent({
        attemptNumber,
        success: true,
      });
    } catch (err) {
      const code = deriveErrorCode(err);
      setErrorCode(code);
      setAttempts(prev => prev + 1);
      setLoading(false);
      serviceContainer.analyticsManager.sendOnboardingAddPcAttemptedEvent({
        attemptNumber,
        success: false,
        errorReason: code,
      });
    }
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxSizing: 'border-box',
        padding: '14px 0px',
      }}
    >
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', opacity: 1 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%', flex: 1, minHeight: 0 }}>
          {/* Title */}
          <p style={{ margin: 0, color: 'rgba(228,228,228,0.92)', fontSize: 20, lineHeight: '20px', fontWeight: 590, textAlign: 'center' }}>
            Add this PC to Playcast
          </p>

          {/* Subtitle */}
          <p style={{ margin: 0, color: 'rgba(228,228,228,0.553)', fontSize: 12, lineHeight: '18px', fontWeight: 400, textAlign: 'center' }}>
            Agree to the terms below to put this computer on the Playcast network and start hosting.
          </p>

          {/* EULA scrollable area */}
          <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              style={{
                flex: 1,
                minHeight: 0,
                overflow: 'auto',
                padding: 12,
                borderRadius: 8,
                border: '1px solid rgba(228,228,228,0.12)',
                background: 'rgba(228,228,228,0.067)',
              }}
            >
              <p style={{ margin: '0 0 10px', color: 'rgba(228,228,228,0.37)', fontSize: 10, lineHeight: '16px', fontWeight: 400, letterSpacing: 0.5 }}>
                END USER LICENSE AGREEMENT
              </p>
              <p style={{ margin: '0 0 6px', color: 'rgba(228,228,228,0.37)', fontSize: 10, lineHeight: '14px', fontWeight: 400 }}>
                Last updated: June 11, 2025 · Version 1.0
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
                {EULA_SECTIONS.map((block, i) => (
                  <EulaBlockRenderer key={i} block={block} />
                ))}
              </div>
            </div>

            {/* Floating scroll-to-bottom bubble */}
            {!hasScrolledToBottom && (
              <button
                type="button"
                onClick={scrollToBottom}
                style={{
                  position: 'absolute',
                  bottom: 12,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(251,191,36,0.9)',
                  color: '#141414',
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
                  zIndex: 10,
                }}
                aria-label="Scroll to bottom"
              >
                ↓
              </button>
            )}
          </div>

          {/* Scroll warning + Agree checkbox */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
            <p style={{
              margin: 0,
              color: 'rgba(251,191,36,0.9)',
              fontSize: 11,
              lineHeight: '14px',
              fontWeight: 500,
              textAlign: 'center',
              visibility: showScrollWarning ? 'visible' : 'hidden',
            }}>
              Please read the full agreement before accepting
            </p>

            <button
              type="button"
              onClick={() => {
                if (!hasScrolledToBottom) {
                  setShowScrollWarning(true);
                  return;
                }
                setAgreed(!agreed);
              }}
              style={{
                width: '100%',
                padding: 10,
                borderRadius: 8,
                border: '1px solid rgba(228,228,228,0.12)',
                background: 'rgba(228,228,228,0.067)',
                cursor: 'pointer',
                textAlign: 'left',
                opacity: hasScrolledToBottom ? 1 : 0.5,
              }}
            >
              <div style={{ display: 'flex', flexFlow: 'row', alignItems: 'center', justifyContent: 'start', gap: 10, width: '100%' }}>
                <div
                  style={{
                    width: 18,
                    height: 18,
                    borderRadius: 4,
                    border: agreed ? '1.5px solid rgb(89,156,231)' : '1.5px solid rgba(228,228,228,0.3)',
                    background: agreed ? 'rgb(89,156,231)' : 'transparent',
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {agreed && (
                    <p style={{ margin: 0, color: 'rgba(228,228,228,0.92)', fontSize: 11, lineHeight: 1, fontWeight: 590 }}>✓</p>
                  )}
                </div>
                <p style={{ margin: 0, color: 'rgba(228,228,228,0.92)', fontSize: 12, lineHeight: '16px', fontWeight: 400 }}>
                  I agree to add this computer to the Playcast network under these terms
                </p>
              </div>
            </button>
          </div>

          {/* Bottom buttons */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, width: '100%' }}>
            <div style={{ opacity: agreed && !loading ? 1 : 0.4 }}>
              <button
                type="button"
                onClick={handleAttest}
                disabled={!agreed || loading}
                style={{
                  width: '100%',
                  minHeight: 44,
                  padding: '12px 16px',
                  borderRadius: 999,
                  border: 'none',
                  background: '#FBBF24',
                  color: '#141414',
                  cursor: agreed && !loading ? 'pointer' : 'default',
                  fontWeight: 600,
                  fontSize: 15,
                }}
              >
                {loading ? 'Adding...' : 'Add this PC'}
              </button>
            </div>
            {onSkip && (
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <button
                  type="button"
                  onClick={onSkip}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: 24,
                    padding: '0 8px',
                    borderRadius: 999,
                    fontSize: 14,
                    lineHeight: '20px',
                    fontWeight: 500,
                    cursor: 'pointer',
                    border: 'none',
                    background: 'transparent',
                    color: 'rgba(228,228,228,0.553)',
                  }}
                >
                  Log out
                </button>
              </div>
            )}
          </div>

          {/* Error modal overlay */}
          {errorCode && (
            <>
              <div style={{ position: 'absolute', inset: 0, borderRadius: 12, background: '#0b0618', opacity: 0.72, zIndex: 60 }} />
              <div style={{ position: 'absolute', inset: 0, zIndex: 70, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
                <div style={{ width: '100%', borderRadius: 10, border: '1px solid rgba(228,228,228,0.15)', background: '#0b0618', padding: 20 }}>
                  <p style={{ margin: '0 0 6px', color: 'rgba(228,228,228,0.92)', fontSize: 16, lineHeight: '20px', fontWeight: 600, textAlign: 'center' }}>
                    Unable to register this PC
                  </p>
                  <p style={{ margin: '0 0 12px', color: 'rgba(228,228,228,0.553)', fontSize: 12, lineHeight: '17px', fontWeight: 400, textAlign: 'center' }}>
                    {retriesExhausted
                      ? 'Registration failed after multiple attempts. Please contact our team on Discord with the error code below.'
                      : 'Something went wrong during machine registration. Share the error code below with our team for help.'}
                  </p>
                  <p style={{ margin: '0 0 16px', color: 'rgba(239,68,68,0.9)', fontSize: 12, lineHeight: '14px', fontWeight: 600, fontFamily: 'monospace', letterSpacing: 0.5, textAlign: 'center' }}>
                    Code: {errorCode}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {!retriesExhausted && (
                      <button
                        type="button"
                        onClick={() => { setErrorCode(null); handleAttest(); }}
                        style={{
                          width: '100%',
                          minHeight: 44,
                          padding: '12px 16px',
                          borderRadius: 999,
                          border: 'none',
                          background: '#FBBF24',
                          color: '#141414',
                          cursor: 'pointer',
                          fontWeight: 600,
                          fontSize: 15,
                        }}
                      >
                        Retry
                      </button>
                    )}
                    <a
                      href="https://discord.gg/playcast"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: retriesExhausted ? 44 : 36,
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: 'none',
                        background: retriesExhausted ? '#FBBF24' : 'rgba(228,228,228,0.1)',
                        color: retriesExhausted ? '#141414' : 'rgba(228,228,228,0.92)',
                        fontSize: 13,
                        fontWeight: retriesExhausted ? 600 : 500,
                        textDecoration: 'none',
                        cursor: 'pointer',
                        boxSizing: 'border-box',
                      }}
                    >
                      Get help on Discord
                    </a>
                    <button
                      type="button"
                      onClick={() => setErrorCode(null)}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        minHeight: 36,
                        padding: '8px 12px',
                        borderRadius: 999,
                        border: 'none',
                        background: 'transparent',
                        color: 'rgba(228,228,228,0.553)',
                        fontSize: 14,
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Back
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
